// Quote feature — select text in an assistant message, quote it into the
// next user message, and forward it as context to the LLM
// (https://www.assistant-ui.com/docs/ui/quote). Hand-rolled rather than
// pulled from the shadcn registry (matches this repo's existing convention
// for tool-group.tsx/reasoning.tsx): `SelectionToolbarPrimitive` already
// ships in @assistant-ui/react, but its built-in `.Quote` button only calls
// `composer.setQuote()` (local/display state). AgentHub's native runner sends
// message content itself, but quote metadata is still a per-request concern, so
// `ComposerQuotePreview` mirrors the quote into `runConfig.custom.quote`.
// `useNativeAgenthubExternalRuntime` forwards that as `forwardedProps.runConfig`,
// which `native_intake.py::_quote_from_forwarded_props` reads.
import { useEffect } from 'react';
import { SelectionToolbarPrimitive, useAui, useAuiState, useMessageQuote } from '@assistant-ui/react';
import { QuoteIcon, XIcon } from 'lucide-react';

/** Renders inside a user message, above `MessagePrimitive.Parts`, when that message quoted an earlier excerpt. */
export function QuoteBlock() {
  const quote = useMessageQuote();
  if (!quote) return null;
  return (
    <blockquote className="mb-1 max-w-[80%] self-end rounded-lg border-l-2 border-primary/50 bg-muted/60 px-3 py-1.5 text-xs text-muted-foreground italic">
      {quote.text}
    </blockquote>
  );
}

/** Floating toolbar shown on text selection inside a message. Place inside `ThreadPrimitive.Root`. */
export function SelectionToolbar() {
  return (
    <SelectionToolbarPrimitive.Root className="z-50 flex items-center gap-1 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md">
      <SelectionToolbarPrimitive.Quote className="flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground">
        <QuoteIcon className="size-3" />
        Quote
      </SelectionToolbarPrimitive.Quote>
    </SelectionToolbarPrimitive.Root>
  );
}

/** Preview of the pending quote inside the composer, before sending. */
export function ComposerQuotePreview() {
  const composerRuntime = useAui().composer;
  const quote = useAuiState((s) => s.composer.quote);

  // Mirror the quote onto `runConfig.custom` so it survives the native wire hop
  // (see the module doc comment above).
  useEffect(() => {
    composerRuntime.setRunConfig(quote ? { custom: { quote } } : {});
  }, [composerRuntime, quote]);

  if (!quote) return null;

  return (
    <div className="mb-1 flex items-start gap-2 rounded-md border-l-2 border-primary/50 bg-muted/60 px-2.5 py-1.5 text-xs text-muted-foreground">
      <QuoteIcon className="mt-0.5 size-3 shrink-0" />
      <span className="line-clamp-2 grow italic">{quote.text}</span>
      <button
        type="button"
        aria-label="Remove quote"
        className="shrink-0 rounded p-0.5 hover:bg-accent hover:text-accent-foreground"
        onClick={() => {
          composerRuntime.setQuote(undefined);
          composerRuntime.setRunConfig({});
        }}
      >
        <XIcon className="size-3" />
      </button>
    </div>
  );
}
